import React, { useEffect, useState } from 'react'
import { SquareCheck, DownloadIcon, Timer, X } from 'lucide-react'
import styles from './KpisTCU.module.css'
import { useDeviceHealthContextData } from '../context/Context'
import {
  downloadTCUKpis,
} from '../../../../../../services/cesl-bus/tcuService'
import { displayValue } from '../../../../../Shared/common'
import { BsBusFrontFill } from 'react-icons/bs'
import { getOverallFrequency, getTotalDevice, getTotalVehicle } from '../DeviceHealthService'
import HealthyUnhealthyPopover from '../HealthyUnhealthyPopover'
import { BUCKET_COLUMNS } from '../frequencyBuckets'

// Rename keys / downloadApi to match your backend
const DEVICE_BUCKETS = [
  { label: 'healthy', key: 'healthyStatus', bucketKey: "healthyVehicleCount", variant: 'bucketHealthy', },
  { label: 'unhealthy', key: 'healthyStatus', bucketKey: "unhealthyVehicleCount", variant: 'bucketUnhealthy' }
]

// Overall Frequency always shows the 7 fixed buckets (0-5 ... >60);
// counts come from overallFrequency (delayWiseVehicleCount, keyed "0-5" ... ">60")
const FREQUENCY_BUCKETS = BUCKET_COLUMNS.map(({ label, field }) => ({
  label,
  key: 'frequencyBucket',
  bucketKey: field
}))

const CARDS = [
  {
    label: 'Total Vehicle ',
    subtitle: 'view all in Device Health',
    icon: BsBusFrontFill,
    key: 'totalVehicle',
    downloadApi: 'downloadTotalVehicleDeviceHealth'
  },
  {
    label: 'Health Status',
    subtitle: 'click to view healthy / unhealthy devices',
    icon: SquareCheck,
    key: 'totalDevice',
    downloadApi: 'downloadTotalAvailableDeviceHealth',
    buckets: DEVICE_BUCKETS
  },
  {
    label: 'Overall Frequency',
    subtitle: 'vehicles per frequency bucket (sec)',
    icon: Timer,
    key: 'frequency',
    downloadApi: 'downloadOverallFrequency',
    buckets: FREQUENCY_BUCKETS,
    colClass: 'col-12 col-lg-6'
  }
]

const COL_CLASS = 'col-12 col-sm-6 col-lg-3'

function useKpiDownload() {
  const { GlobalFilter, setGlobalFilters } = useDeviceHealthContextData()

  return (api, name) => {
    downloadTCUKpis(api, GlobalFilter)
      .then(data => {
        const url = window.URL.createObjectURL(data)

        const link = document.createElement('a')
        link.href = url
        link.download = `${name}.csv`

        document.body.appendChild(link)
        link.click()
        link.remove()

        window.URL.revokeObjectURL(url)
      })
      .catch(() => { })
  }
}

function StatCard({ label, value, subtitle, icon: Icon, downloadApi }) {
  const downloadKpiData = useKpiDownload()

  return (
    <div className={COL_CLASS}>
      <div
        className={`${styles.card} cursor-pointer`}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={subtitle}
      >
        {/* LEFT */}
        <div className={styles.cardContent}>
          <div className={styles.labelRow}>
            <span className={styles.label}>{label}</span>

            <DownloadIcon
              onClick={() => downloadKpiData(downloadApi, label)}
              className={`${styles.downloadIcon} custom-Btn-animation`}
              size={13}
            />
          </div>

          <div className={styles.value}>{displayValue(value)}</div>
        </div>

        {/* RIGHT */}
        <div className={styles.iconWrapper}>
          <Icon className={styles.icon} />
        </div>
      </div>
    </div>
  )
}

function BucketCard({
  label,
  subtitle,
  icon: Icon,
  downloadApi,
  buckets,
  colClass = COL_CLASS,
  data,
  status,
  setStatus
}) {
  const downloadKpiData = useKpiDownload()
  const { GlobalFilter, setGlobalFilters } = useDeviceHealthContextData()

  // only the Overall Frequency card filters the page by bucket
  const isFrequencyCard = buckets.some(b => b.key === 'frequencyBucket')
  const selectedBucket = isFrequencyCard ? GlobalFilter?.frequency || "" : ""

  const clearBucketFilter = () => setGlobalFilters(prev => ({ ...prev, "frequency": "" }))

  function subKPIHandlerFunction(label) {
    if (label.includes("ealthy")) {
        setStatus(label)

    } else {
      // clicking the selected bucket again removes the bucket filter
      setGlobalFilters(prev => ({ ...prev, "frequency": prev.frequency === label ? "" : label }))
    }
  }

  return (
    <div className={colClass}>
      <div
        className={`${styles.card} ${styles.cardTall}`}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={subtitle}
      >
        {/* TOP: label + icon */}
        <div className={styles.topRowCompact}>
          <div className={styles.labelRow}>
            <span className={styles.label}>{label}</span>

            <DownloadIcon
              onClick={() => downloadKpiData(downloadApi, label)}
              className={`${styles.downloadIcon} custom-Btn-animation`}
              size={13}
            />

            {selectedBucket && (
              <button
                type="button"
                className={styles.bucketClearBtn}
                title="Remove the bucket filter"
                onClick={clearBucketFilter}
              >
                {selectedBucket}
                <X size={11} />
              </button>
            )}
          </div>

          <Icon className={styles.iconSmall} />
        </div>

        <div className={styles.bucketRow}>
          {buckets.map((b, i) => (
            <div
              key={b.key + i}
              className={`${styles.bucket} ${b.variant ? styles[b.variant] : ''} ${selectedBucket === b.label ? styles.bucketSelected : ''}`}
              data-toggle={b.label.includes("ealthy") ? "modal" : ""}
              data-target="#HealthyUnhealthyPopover"
              onClick={() => subKPIHandlerFunction(b.label)}
            >
              <div className={styles.bucketText}>
                <span className={styles.bucketValue}>
                  {displayValue(data?.[b.key]?.[b.bucketKey])}
                </span>
                {/* bucket name under the value */}
                <div className={styles.bucketFooter}>
                  <span className={styles.bucketName}>{b.label}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}


export default function KpisTCU() {
  const { GlobalFilter, setGlobalFilters } = useDeviceHealthContextData()

  const [kpiData, setKpiData] = useState({})
  const [status, setStatus] = useState("")

  useEffect(() => {
    fetchData()
  }, [GlobalFilter])

  const fetchData = async () => {

    const results = await Promise.allSettled([
      getTotalVehicle(GlobalFilter),
      getTotalDevice(GlobalFilter),
      getOverallFrequency(GlobalFilter)
    ]);

    setKpiData({
      totalVehicle:
        results[0].status === "fulfilled"
          ? results?.[0]?.value?.totalVehicleCount || null
          : null,

      totalDevice:
        results[1].status === "fulfilled"
          ? results?.[1]?.value.totalVehicleCount || null
          : null,

      healthyStatus:
        results[1].status === "fulfilled"
          ? results?.[1]?.value || {}
          : {},

      frequency:
        results[2].status === "fulfilled"
          ? results[2].value?.totalVehicleCount || null
          : null,
      // Overall Frequency sub cards: overallFrequency -> delayWiseVehicleCount { "0-5": n, ..., ">60": n }
      frequencyBucket:
        results[2].status === "fulfilled"
          ? results[2].value?.delayWiseVehicleCount || {}
          : {}
    });
  };

  const data = { ...kpiData }
  return (
    <div className="container-fluid py-2">
      <div className="row g-2">
        {CARDS.map(card =>
          card.buckets ? (
            <BucketCard
              key={card.label}
              data={data}
              status={status}
              setStatus={setStatus}
              {...card}
            />
          ) : (
            <StatCard
              key={card.label}
              value={data?.[card.key]}
              {...card}
            />
          )
        )}
      </div>
      <HealthyUnhealthyPopover status={status} />
    </div>
  )
}